import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  CalendarDays, Users, FileText, Sparkles, MessageCircle, CalendarPlus, Image as ImageIcon,
  Landmark, TrendingDown, UserPlus, TrendingUp, ArrowUpRight, ArrowDownRight, ChevronRight,
  Wallet, Target, CheckCircle2, Loader2, Bell, ShoppingBag, Clock,
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, Cell,
} from "recharts";
import { toast } from "sonner";
import WeatherWidget from "@/components/WeatherWidget";
import MesEspaces from "@/components/MesEspaces";
import { TodayWidget, TasksWidget, StartWidget } from "@/components/DashWidgets";
import CommandCenter from "@/components/CommandCenter";
import { apptPeriod } from "@/lib/apptPeriod";
import { useDashPrefs } from "@/lib/dashboardPrefs";

const eur = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);
const eur2 = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n || 0);
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

const CC = {
  cyan: "bg-cyan-100 text-cyan-600", violet: "bg-violet-100 text-violet-600",
  green: "bg-emerald-100 text-emerald-600", orange: "bg-orange-100 text-orange-600",
  yellow: "bg-yellow-100 text-yellow-600", turquoise: "bg-teal-100 text-teal-600",
  pink: "bg-pink-100 text-pink-600", blue: "bg-blue-100 text-blue-600",
  red: "bg-red-100 text-red-600", indigo: "bg-indigo-100 text-indigo-600",
};

const QUICK = [
  { label: "Calendrier", desc: "RDV, devis, multi-jours, signatures", icon: CalendarDays, to: "/calendar", c: "cyan" },
  { label: "Clients", desc: "Fiches, véhicules, historique", icon: Users, to: "/clients", c: "violet" },
  { label: "Factures & Devis", desc: "Factures PDF, devis, relances", icon: FileText, to: "/invoices", c: "green" },
  { label: "Prestations", desc: "Catalogue, catégories, tarifs", icon: Sparkles, to: "/services", c: "orange" },
  { label: "SMS", desc: "SMS et rappels", icon: MessageCircle, to: "/mes-sms", c: "yellow" },
  { label: "Page de réservation", desc: "Gérer ma page de prise de RDV", icon: CalendarPlus, to: "/page-reservation", c: "turquoise" },
  { label: "Galerie", desc: "Photos avant / après", icon: ImageIcon, to: "/galerie", c: "pink" },
  { label: "Comptabilité", desc: "Encaissements, cotisations", icon: Landmark, to: "/comptabilite", c: "blue" },
  { label: "Dépenses", desc: "Charges et achats", icon: TrendingDown, to: "/expenses", c: "red" },
  { label: "Prospects", desc: "Suivi commercial", icon: UserPlus, to: "/prospects", c: "indigo" },
];

function Delta({ v, suffix = "%" }) {
  if (v === null || v === undefined) return null;
  const up = v >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${up ? "text-emerald-600" : "text-red-500"}`}>
      {up ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
      {up ? "+" : ""}{v}{suffix}
    </span>
  );
}

const MONTH_LABEL = (iso) => { const d = new Date(iso); return { m: ["janv.","févr.","mars","avr.","mai","juin","juil.","août","sept.","oct.","nov.","déc."][d.getMonth()], day: d.getDate() }; };
const timeOf = (iso) => new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "-");

export default function Dashboard() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [range, setRange] = useState("month");
  const [chartType, setChartType] = useState("area");
  const [goalOpen, setGoalOpen] = useState(false);
  const [goalVal, setGoalVal] = useState("");
  const hour = new Date().getHours();
  const { prefs } = useDashPrefs();
  const W = (k) => { const ws = prefs?.widgets || []; const i = ws.findIndex((w) => w.key === k); const hidden = i >= 0 && ws[i].visible === false; return { order: i < 0 ? 50 : i + 1, display: hidden ? "none" : undefined }; };
  const greeting = hour < 12 ? "Bonjour" : hour < 18 ? "Bon après-midi" : "Bonsoir";

  const load = useCallback(() => { api.get("/dashboard/overview").then((r) => setD(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const saveGoal = async () => {
    try { await api.put("/settings/monthly-goal", { monthly_objective: Number(goalVal || 0) }); toast.success("Objectif mis à jour"); setGoalOpen(false); load(); }
    catch { toast.error("Enregistrement impossible"); }
  };

  if (!d) return <div className="flex items-center justify-center h-64"><Loader2 className="w-7 h-7 animate-spin text-emerald-600" /></div>;

  const o = d.objective;
  const evo = d.evolution[range] || [];
  const evoTotal = evo.reduce((s, x) => s + (x.revenue || 0), 0);
  const evoAvg = evo.length ? evoTotal / evo.length : 0;
  const denom = o.objectif || o.prevision || 1;
  const maxTop = Math.max(...(d.top_services.map((t) => t.count) || [1]), 1);
  const maxDay = Math.max(...(d.last7.days.map((x) => x.revenue) || [1]), 1);

  return (
    <div className="flex flex-col gap-6" data-testid="dashboard-root">
      {/* Header */}
      <div style={{ order: 0 }}>
        <div className="text-sm text-slate-500">{greeting} 👋</div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-slate-900" data-testid="dashboard-company-name">{d.company_name || "Mon tableau de bord"}</h1>
      </div>
      <div style={{ order: 0 }}><CommandCenter /></div>

      {/* Météo locale (ville configurée dans Paramètres) */}
      <div style={W("weather")} data-testid="w-weather"><WeatherWidget /></div>

      {/* Accès rapide */}
      <div style={W("quick")}>
        <div className="text-sm font-semibold text-slate-700 mb-3">Accès rapide</div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="quick-access">
          {QUICK.map((q) => (
            <button key={q.label} onClick={() => navigate(q.to)} data-testid={`quick-${q.to.replace(/\W+/g, "-")}`}
              className="group bg-white rounded-2xl p-4 text-left border border-slate-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all flex items-start gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${CC[q.c]}`}><q.icon className="w-5 h-5" /></div>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] sm:text-sm font-semibold text-slate-900 leading-tight break-words">{q.label}</div>
                <div className="hidden sm:block text-[11px] text-slate-400 mt-0.5 line-clamp-2">{q.desc}</div>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-slate-500 shrink-0 mt-1 hidden sm:block" />
            </button>
          ))}
        </div>
      </div>

      <div style={W("spaces")}><MesEspaces /></div>
      {prefs && W("start").display !== "none" && <div style={W("start")}><StartWidget /></div>}
      {prefs && W("today").display !== "none" && <div style={W("today")}><TodayWidget /></div>}
      {prefs && W("tasks").display !== "none" && <div style={W("tasks")}><TasksWidget /></div>}

      {/* Titre dashboard dynamique */}
      <div className="text-center pt-2" style={W("kpi")}>
        <h2 className="font-heading text-lg font-bold text-slate-900">📊 Tableau de bord — {cap(d.month_label)}</h2>
      </div>

      {/* 4 indicateurs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="kpi-grid" style={W("kpi")}>
        <KpiCard testid="kpi-ca" bg="bg-emerald-50" ring="ring-emerald-100" icon={TrendingUp} ic="bg-emerald-100 text-emerald-600"
          label="CA encaissé ce mois" value={eur(d.ca_month)} foot={<><Delta v={d.ca_change_pct} /> <span className="text-slate-400">vs mois dernier</span></>} />
        <KpiCard testid="kpi-rdv" bg="bg-blue-50" ring="ring-blue-100" icon={CalendarDays} ic="bg-blue-100 text-blue-600"
          label="RDV terminés ce mois" value={`${d.appts_done} / ${d.appts_total}`} foot={<span className="text-slate-500">{d.completion_rate}% réalisés</span>} />
        <KpiCard testid="kpi-clients" bg="bg-violet-50" ring="ring-violet-100" icon={Users} ic="bg-violet-100 text-violet-600"
          label="Clients ce mois" value={d.clients_month} foot={<span className="text-emerald-600 font-semibold">+{d.new_clients_month} nouveau{d.new_clients_month > 1 ? "x" : ""}</span>} />
        <KpiCard testid="kpi-basket" bg="bg-orange-50" ring="ring-orange-100" icon={ShoppingBag} ic="bg-orange-100 text-orange-600"
          label="Panier moyen ce mois" value={eur2(d.avg_basket)} foot={<><Delta v={d.avg_basket_change_pct} /> <span className="text-slate-400">vs mois dernier</span></>} />
      </div>

      {/* Objectif du mois */}
      <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid="objective-block" style={W("objective")}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2"><Target className="w-5 h-5 text-emerald-600" /><h3 className="font-heading text-base font-bold text-slate-900">Objectif {cap(d.month_label)}</h3></div>
          <Button size="sm" variant="outline" data-testid="btn-edit-goal" onClick={() => { setGoalVal(String(o.objectif || "")); setGoalOpen(true); }} className="border-slate-200 h-8">Modifier</Button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <SubStat label="🟢 Encaissé" value={eur(o.encaisse)} tone="text-emerald-600" />
          <SubStat label="🟡 En attente" value={eur(o.en_attente)} tone="text-yellow-600" />
          <SubStat label="🔵 Planifié" value={eur(o.planifie)} tone="text-blue-600" />
          <SubStat label="🎯 Objectif" value={o.objectif ? eur(o.objectif) : "—"} tone="text-slate-900" />
        </div>
        <div className="h-3.5 rounded-full bg-slate-100 overflow-hidden flex mb-2" data-testid="objective-bar">
          <div className="h-full bg-emerald-500" style={{ width: `${Math.min(o.encaisse / denom * 100, 100)}%` }} />
          <div className="h-full bg-yellow-400" style={{ width: `${Math.min(o.en_attente / denom * 100, 100)}%` }} />
          <div className="h-full bg-blue-500" style={{ width: `${Math.min(o.planifie / denom * 100, 100)}%` }} />
        </div>
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
          <span>Prévision : <b className="text-slate-800">{eur(o.prevision)}</b> / {o.objectif ? eur(o.objectif) : "objectif non défini"}</span>
          <span className="flex gap-3">
            <span className="text-emerald-600">Encaissé {o.pct_encaisse}%</span>
            <span className="text-yellow-600">Attente {o.pct_attente}%</span>
            <span className="text-blue-600">Planifié {o.pct_planifie}%</span>
          </span>
        </div>
        <div className="mt-3 text-sm font-medium">
          {o.objectif ? (o.reste > 0
            ? <span className="text-slate-700">💪 Encore <b className="text-emerald-600">{eur(o.reste)}</b> à générer pour atteindre l'objectif.</span>
            : <span className="text-emerald-600">🎉 Objectif atteint, bravo !</span>)
            : <span className="text-slate-400">Définissez un objectif mensuel pour suivre votre progression.</span>}
        </div>
        <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-slate-100">
          <div><div className="text-xs text-slate-400">Panier moyen</div><div className="font-mono font-semibold text-slate-900 flex items-center gap-2">{eur2(d.avg_basket)} <Delta v={d.avg_basket_change_pct} /></div></div>
          <div><div className="text-xs text-slate-400">Nouveaux clients</div><div className="font-mono font-semibold text-slate-900">+{d.new_clients_month} <span className="text-xs text-slate-400 font-sans">ce mois</span></div></div>
        </div>
      </div>

      {/* Prochains RDV + Alertes */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" style={W("upcoming")}>
        <div className="lg:col-span-2 bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid="upcoming-block">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2"><div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center"><CalendarDays className="w-4 h-4" /></div><h3 className="font-heading text-base font-bold text-slate-900">Prochains RDV <span className="text-slate-400 font-normal">({d.upcoming_count})</span></h3></div>
            <button onClick={() => navigate("/calendar")} className="text-sm text-emerald-600 font-semibold hover:underline flex items-center gap-1">Calendrier <ChevronRight className="w-4 h-4" /></button>
          </div>
          {d.upcoming.length ? (
            <div className="space-y-2">
              {d.upcoming.map((a) => { const dl = MONTH_LABEL(a.start); return (
                <button key={a.id} onClick={() => navigate("/calendar")} data-testid={`upcoming-${a.id}`} className="w-full flex items-center gap-3 p-2.5 rounded-xl hover:bg-slate-50 transition text-left">
                  <div className="w-12 h-12 rounded-xl bg-slate-100 flex flex-col items-center justify-center shrink-0">
                    <span className="text-[10px] uppercase text-slate-400 leading-none">{dl.m}</span>
                    <span className="text-lg font-bold text-slate-800 leading-tight">{dl.day}</span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-2">{a.service_color && <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: a.service_color }} />}{a.client_name || a.title || "RDV"}</div>
                    <div className="text-xs text-slate-500" data-testid={`upcoming-when-${a.id}`}>{apptPeriod(a.start, a.end) ? `${apptPeriod(a.start, a.end)} · multi-jours` : timeOf(a.start)}</div>
                  </div>
                  <div className="text-right shrink-0"><div className="font-mono font-semibold text-slate-900 text-sm">{eur2(a.amount)}</div></div>
                  <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                </button>
              ); })}
            </div>
          ) : <p className="text-sm text-slate-500 py-6 text-center">Aucun rendez-vous à venir.</p>}
        </div>

        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid="alerts-block">
          <div className="flex items-center gap-2 mb-4"><Bell className="w-4 h-4 text-slate-500" /><h3 className="font-heading text-base font-bold text-slate-900">Alertes</h3></div>
          {d.alerts.length === 0 ? (
            <div className="bg-emerald-50 rounded-xl p-5 text-center" data-testid="alerts-ok">
              <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
              <div className="font-semibold text-emerald-700">Tout est en ordre !</div>
              <div className="text-xs text-emerald-600/80 mt-0.5">Aucune alerte à signaler</div>
            </div>
          ) : (
            <div className="space-y-2">
              {d.alerts.map((al) => (
                <button key={al.type} onClick={() => navigate(al.link)} data-testid={`alert-${al.type}`} className="w-full flex items-center justify-between gap-2 p-3 rounded-xl bg-amber-50 hover:bg-amber-100 transition text-left">
                  <span className="text-sm text-amber-800">{al.label}</span>
                  <ChevronRight className="w-4 h-4 text-amber-500 shrink-0" />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 7 derniers jours + Activité récente */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5" style={W("last7")}>
        <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid="last7-block">
          <div className="flex items-center justify-between mb-1"><h3 className="font-heading text-base font-bold text-slate-900">7 derniers jours</h3></div>
          <div className="text-2xl font-bold font-mono text-emerald-600 mb-4">{eur(d.last7.total)}</div>
          <div data-testid="last7-chart">
            <div className="flex items-end justify-between gap-1.5 h-24">
              {d.last7.days.map((x) => (
                <div key={x.date} className="flex-1 h-full flex flex-col justify-end" title={`${x.label} · ${eur2(x.revenue)}`} data-testid={`last7-bar-${x.date}`}>
                  <div className="w-full rounded-t" style={{ height: `${x.revenue > 0 ? Math.max((x.revenue / maxDay) * 100, 6) : 4}%`, background: x.revenue > 0 ? "#34d399" : "#e2e8f0" }} />
                </div>
              ))}
            </div>
            <div className="flex justify-between gap-1.5 mt-1">
              {d.last7.days.map((x) => (
                <span key={x.date} className="flex-1 text-center text-[10px] text-slate-400">{x.label}</span>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid="activity-block">
          <h3 className="font-heading text-base font-bold text-slate-900 mb-4">Activité récente</h3>
          {d.activity.length ? (
            <div className="divide-y divide-slate-100">
              {d.activity.map((a, i) => (
                <div key={i} className="flex items-center justify-between py-2.5">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: a.service_color || "#cbd5e1" }} />
                    <div className="min-w-0"><div className="text-sm text-slate-900 truncate">{a.client}</div><div className="text-xs text-slate-400">{fmtWhen(a.when)}</div></div>
                  </div>
                  <StatusBadge status={a.status} />
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-slate-500 py-6 text-center">Aucune activité récente.</p>}
        </div>
      </div>

      {/* Évolution CA */}
      <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid="evolution-block" style={W("evolution")}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div><h3 className="font-heading text-base font-bold text-slate-900">Évolution du chiffre d'affaires</h3>
            <div className="text-xs text-slate-500 mt-0.5">Total <b className="text-slate-800">{eur(evoTotal)}</b> · Moyenne <b className="text-slate-800">{eur(evoAvg)}</b></div></div>
          <div className="flex items-center gap-2">
            <div className="flex bg-slate-100 rounded-lg p-0.5">
              {[["week", "Semaine"], ["month", "Mois"], ["year", "Année"]].map(([k, l]) => (
                <button key={k} onClick={() => setRange(k)} data-testid={`evo-range-${k}`} className={`px-3 py-1 text-xs font-medium rounded-md transition ${range === k ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>{l}</button>
              ))}
            </div>
            <div className="flex bg-slate-100 rounded-lg p-0.5">
              {[["area", "Aire"], ["bar", "Barres"]].map(([k, l]) => (
                <button key={k} onClick={() => setChartType(k)} data-testid={`evo-type-${k}`} className={`px-3 py-1 text-xs font-medium rounded-md transition ${chartType === k ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>{l}</button>
              ))}
            </div>
          </div>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%" minHeight={220}>
            {chartType === "area" ? (
              <AreaChart data={evo} margin={{ left: -18, right: 5, top: 5 }}>
                <defs><linearGradient id="revg" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10b981" stopOpacity={0.35} /><stop offset="100%" stopColor="#10b981" stopOpacity={0} /></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="label" tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<EvoTip />} />
                <Area type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} fill="url(#revg)" />
              </AreaChart>
            ) : (
              <BarChart data={evo} margin={{ left: -18, right: 5, top: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="label" tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                <YAxis tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<EvoTip />} cursor={{ fill: "#f8fafc" }} />
                <Bar dataKey="revenue" radius={[4, 4, 0, 0]} fill="#10b981" />
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top prestations */}
      <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid="top-services-block" style={W("top_services")}>
        <div className="flex items-center justify-between mb-1"><h3 className="font-heading text-base font-bold text-slate-900">Top prestations</h3><span className="text-xs text-slate-400">{cap(d.month_label)}</span></div>
        <div className="text-xs text-slate-500 mb-4">{d.top_services_total_count} prestation(s) réalisée(s) ce mois · {eur(d.top_services_total_ca)}</div>
        {d.top_services.length ? (
          <div className="space-y-3">
            {d.top_services.map((t, i) => (
              <div key={i} data-testid={`top-service-${i}`} className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold shrink-0" style={{ background: `${t.color}22`, color: t.color }}>#{i + 1}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="text-sm text-slate-800 truncate">{t.name}</span>
                    <span className="text-xs text-slate-500 shrink-0">{t.count}× · <b className="text-slate-700">{eur(t.revenue)}</b></span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(t.count / maxTop) * 100}%`, background: t.color }} /></div>
                </div>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-slate-500 py-6 text-center">Aucune prestation facturée ce mois.</p>}
        <p className="text-[11px] text-slate-400 mt-3">Montant des prestations des RDV réalisés ce mois (statuts à facturer / en attente / payé). Distinct du CA encaissé, qui ne compte que les paiements réellement reçus.</p>
      </div>

      {/* Mois en détail */}
      <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm" data-testid="month-detail-block" style={W("month_detail")}>
        <h3 className="font-heading text-base font-bold text-slate-900 mb-4">{cap(d.month_label.split(" ")[0])} en détail</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Detail label="RDV actifs" value={d.detail.rdv_actifs} />
          <Detail label="Terminés / Planifiés" value={`${d.detail.termines} / ${d.detail.planifies}`} />
          <Detail label="Taux de réalisation" value={`${d.detail.completion_rate}%`} tone="text-emerald-600" />
          <Detail label="Absences" value={d.detail.absences} tone="text-red-500" />
          <Detail label="CA / jour" value={eur2(d.detail.ca_per_day)} />
          <Detail label="Clients du mois" value={d.detail.clients_month} />
          <Detail label="Nouveaux clients" value={`+${d.detail.new_clients_month}`} tone="text-violet-600" />
          <Detail label="URSSAF estimée" value={eur2(d.urssaf.amount)} tone="text-slate-700" />
        </div>
        <p className="text-[11px] text-slate-400 mt-3">URSSAF = CA encaissé × {d.urssaf.rate}% (source unique, identique à la page Comptabilité).</p>
      </div>

      <Dialog open={goalOpen} onOpenChange={setGoalOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-sm"><DialogHeader><DialogTitle className="text-slate-900">Objectif mensuel</DialogTitle><DialogDescription className="text-slate-500">Montant de chiffre d'affaires à atteindre ce mois-ci.</DialogDescription></DialogHeader>
          <div className="space-y-2"><label className="text-xs text-slate-500">Montant cible (€)</label><Input type="number" data-testid="goal-input" value={goalVal} onChange={(e) => setGoalVal(e.target.value)} className="bg-slate-50 border-slate-200" /></div>
          <DialogFooter><Button variant="ghost" onClick={() => setGoalOpen(false)}>Annuler</Button><Button data-testid="goal-save" onClick={saveGoal} className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold">Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiCard({ bg, ring, icon: Icon, ic, label, value, foot, testid }) {
  return (
    <div className={`${bg} rounded-2xl p-4 ring-1 ${ring}`} data-testid={testid}>
      <div className="flex items-start justify-between">
        <span className="text-[11px] uppercase tracking-wide text-slate-500 font-medium leading-tight">{label}</span>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${ic}`}><Icon className="w-4 h-4" /></div>
      </div>
      <div className="text-2xl md:text-3xl font-bold font-mono text-slate-900 tracking-tight mt-3">{value}</div>
      <div className="mt-1 text-xs flex items-center gap-1 flex-wrap">{foot}</div>
    </div>
  );
}
function SubStat({ label, value, tone }) {
  return <div className="bg-slate-50 rounded-xl p-3"><div className="text-[11px] text-slate-500">{label}</div><div className={`font-mono font-bold mt-0.5 ${tone}`}>{value}</div></div>;
}
function Detail({ label, value, tone = "text-slate-900" }) {
  return <div><div className="text-xs text-slate-400">{label}</div><div className={`font-mono font-bold text-lg mt-0.5 ${tone}`}>{value}</div></div>;
}
function EvoTip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-white rounded-lg shadow-lg border border-slate-200 px-3 py-2 text-xs">
      <div className="font-semibold text-slate-900">{p.date}</div>
      <div className="text-emerald-600 font-mono">{eur2(p.revenue)}</div>
      <div className="text-slate-400">{p.count} facture{p.count > 1 ? "s" : ""}</div>
    </div>
  );
}
